// The upload queue as state, and the three-step flow that drains it.
//
// Each queued file goes request -> upload to storage on the contract -> confirm, one at a time:
// a phone on a site connection does better with one transfer in flight than five, and a failure
// then lands on one file with one reason rather than on all of them at once. The queue is drained
// only when there is a row to attach to, which the caller decides - at once on an existing row,
// after the first save on a new one.
//
// What a file is attached to is the caller's: `api` holds the request and confirm calls - and a
// `validate` when its file policy is not the tracker's - and `drain` passes its arguments through
// to both calls: a leg and a row for the tracker, a counterparty for its documents
// (TX-US-0033).
//
// Each item keeps the File it was given, never a copy or a replacement: a deal document's type is
// remembered against that File (documents/documentTypes.js rememberType), and the request reads it
// back from the same object.

import { useCallback, useRef, useState } from 'react';

import { confirmAttachmentUpload, requestAttachmentUpload } from '../../api.js';
import { putDocument } from '../../documents/files.js';
import { STATES, enqueue, queued, remove, update, withoutDone } from './uploads.js';

const DELIVERY_API = { request: requestAttachmentUpload, confirm: confirmAttachmentUpload };

export default function useAttachmentUploads(api = DELIVERY_API) {
  const [items, setItems] = useState([]);
  const [draining, setDraining] = useState(false);
  // What the queue holds right now, readable from inside an async drain without a render in
  // between: state is what the list renders, the ref is what the drain reads.
  const latest = useRef(items);
  latest.current = items;

  // Copied out of the FileList at once: it is a live view of the input, and the input is reset
  // right after the change so the same file can be chosen twice - by the time the state updater
  // runs, the list would be empty.
  const add = useCallback((files) => {
    const chosen = Array.from(files);
    setItems((current) => enqueue(current, chosen, api.validate));
  }, [api]);
  const drop = useCallback((key) => setItems((current) => remove(current, key)), []);

  // Returns the attachments that landed, and whether any file failed. The items themselves carry
  // the per-file outcome for the list to show.
  const drain = useCallback(async (...target) => {
    const pending = queued(latest.current);
    if (pending.length === 0) return { uploaded: [], failed: false };

    setDraining(true);
    const uploaded = [];
    let failed = false;
    try {
      for (const item of pending) {
        setItems((current) => update(current, item.key, { state: STATES.uploading }));
        try {
          const contract = await api.request(...target, item.file);
          await putDocument(
            item.file, contract.upload_url, contract.required_headers, contract.upload_method,
          );
          const attachment = await api.confirm(...target, contract.attachment.attachment_id);
          uploaded.push(attachment);
          setItems((current) => update(current, item.key, { state: STATES.done, attachment }));
        } catch (err) {
          failed = true;
          setItems((current) => update(current, item.key, {
            state: STATES.failed,
            error: err.message,
          }));
        }
      }
    } finally {
      setDraining(false);
      setItems((current) => withoutDone(current));
    }
    return { uploaded, failed };
  }, [api]);

  return { items, draining, add, drop, drain, pending: queued(items).length };
}
